<script lang="ts">
import Button1XP from '../Button1XP/Button1XP.vue';
import Icon from '../Icon/Icon.vue';
import { computed, onBeforeMount, ref, watch } from 'vue';
import * as Storage from '../../shared/Storage';
import type { Instance, WindowWika } from '../../types/window';
import { useStoreCountry } from '../../store/country/country';
import { useStoreTranslations } from '../../store/translations/translations';
import safeFetch from '../../utils/safeFetch';

type Props = {
  // TODO: Check with BE if corporate text comes from config service, translation service
  corporateText?: string;
  corporateShopUrl: string;
  corporateWebsiteUrl: string;
  geoIpServiceUrl: string;
  instance: Instance;
  isCorporateWebsite?: boolean;
  websiteCountry: string;
};
</script>

<script setup lang="ts">
const {
  corporateShopUrl,
  corporateText,
  corporateWebsiteUrl,
  geoIpServiceUrl,
  instance,
  isCorporateWebsite,
  websiteCountry,
} = defineProps<Props>();

const countries = useStoreCountry();

const storeTranslations = useStoreTranslations();

const GEOIP_COOKIE = 'hideGeoIpOverlayFor';

const _window = window as unknown as WindowWika;

const isOverlayVisible = ref(_window.isStorybook);
const cookieName = ref('');
const text = ref('');
const changeUrl = ref('');
const changeName = ref('');
const currentCountry = ref('');
const websiteCountryRef = ref(websiteCountry);
const geoIpData = ref(null);

const emit = defineEmits(['change']);

const initGeoIP = () => {
  safeFetch(geoIpServiceUrl)
    .then((response) => {
      if (!response) {
        // TODO: add handling for undefined
        return;
      }
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then((data) => {
      geoIpData.value = data[0];
    })
    .catch((error) => {
      console.error(error);
    });
};

const checkGeoIp = (data) => {
  currentCountry.value = data.country_code;
  const countriesItems = countries.items?.countries;

  if (
    countriesItems &&
    currentCountry.value &&
    currentCountry.value !== 'unknown' &&
    currentCountry.value !== websiteCountryRef.value &&
    currentCountry.value !== cookieName.value &&
    websiteCountryRef.value !== cookieName.value
  ) {
    Object.keys(countriesItems).forEach((countryKey) => {
      if (countryKey === currentCountry.value) {
        const { url, name } = countriesItems[countryKey];

        if (!url && corporateWebsiteUrl && !isCorporateWebsite) {
          changeUrl.value =
            instance === 'shop' && corporateShopUrl ? corporateShopUrl : corporateWebsiteUrl;

          text.value = corporateText
            ? corporateText
            : storeTranslations.translations
              ? `${storeTranslations.translations['geoIpOverlay.title'] || 'Are you in the right place?'} ${storeTranslations.translations['geoIpOverlay.text'] || 'We offer a local website for'} ${changeName.value}`
              : `Are you in the right place? We offer a local website for ${name}`;
        } else {
          changeUrl.value = url;
          changeName.value = name;

          text.value = storeTranslations.translations
            ? `${storeTranslations.translations['geoIpOverlay.title'] || 'Are you in the right place?'} ${storeTranslations.translations['geoIpOverlay.text'] || 'We offer a local website for'} ${changeName.value}`
            : `Are you in the right place? We offer a local website for ${changeName.value}`;
        }
      }
    });

    if (
      !Object.hasOwn(countriesItems, currentCountry.value) &&
      !!corporateWebsiteUrl &&
      !isCorporateWebsite
    ) {
      changeUrl.value =
        instance === 'shop' && corporateShopUrl ? corporateShopUrl! : corporateWebsiteUrl;

      text.value = corporateText
        ? corporateText
        : storeTranslations.translations
          ? `${storeTranslations.translations['geoIpOverlay.title'] || 'Are you in the right place?'} ${storeTranslations.translations['geoIpOverlay.text'] || 'We offer a local website for'} ${changeName.value}`
          : `Are you in the right place? We offer a local website for ${changeName.value}`;
    }

    const countryName = countriesItems?.[currentCountry.value]?.name;

    if (countryName && websiteCountry && countryName === websiteCountry) {
      return;
    }

    if (changeUrl.value) {
      try {
        const targetUrlObj = new URL(changeUrl.value);
        const targetPath = targetUrlObj.pathname;
        const currentPath = window.location.pathname;

        if (targetPath !== '/' && currentPath.includes(targetPath)) {
          return;
        }
      } catch (error) {
        console.error(error);
        if (window.location.href.indexOf(changeUrl.value) > -1) {
          return;
        }
      }
    }

    if (storeTranslations.translations) {
      setTimeout(() => {
        isOverlayVisible.value = true;
      }, 600);
    }
  }
};

const handleClose = () => {
  Storage.setSessionStorage(GEOIP_COOKIE, websiteCountryRef.value);
  isOverlayVisible.value = false;
};

watch([() => storeTranslations.translations, geoIpData], ([translations, geoData]) => {
  if (translations && geoData) {
    checkGeoIp(geoData);
  }
});

onBeforeMount(() => {
  cookieName.value = Storage.getSessionStorage(GEOIP_COOKIE);
  const queries = window.location.search.substring(1).split('&');
  if (queries) {
    for (const query of queries) {
      if (query.includes('dssheet')) {
        return;
      }
    }
  }

  initGeoIP();
});

const isVisible = computed(() =>
  Boolean(isOverlayVisible.value && changeUrl.value.length > 0 && storeTranslations.translations),
);

watch(isVisible, () => {
  window.setTimeout(() => {
    emit('change', isOverlayVisible.value);
  }, 50);
});
</script>

<template>
  <div v-if="isVisible" :class="$style.container">
    <div :class="$style.text">
      <Icon name="info" />
      <span>{{ text }}</span>
    </div>
    <div :class="$style.buttonContainer">
      <Button1XP
        key="id"
        size="xs"
        :text="storeTranslations.translations?.['geoIpOverlay.changeButtonText'] || 'Change now'"
        :url="changeUrl"
        variant="primary"
      />
      <Button1XP
        key="id2"
        size="xs"
        :text="storeTranslations.translations?.['geoIpOverlay.stayButtonText'] || 'Stay here'"
        variant="secondary"
        @click="handleClose"
      />
    </div>
  </div>
</template>

<style module lang="scss" src="./GeoIPOverlay1XP.scss" />
